import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Users, Building2, Menu, X, ChevronLeft, ChevronRight, LogOut, ListChecks, ClipboardList, Settings, Smartphone, Inbox, MessageSquare, PhoneCall, Globe, Receipt, Home } from 'lucide-react';
import axios from 'axios';
import { IncomingCall } from '@/components/IncomingCall';
import { CallbackAlerts } from '@/components/CallbackAlerts';
import { PushToggle } from '@/components/PushToggle';
import { Button } from '@/components/ui/button';
import { ThemeCustomizer, applyTheme, getTheme } from '@/components/ThemeCustomizer';
import { useAuth } from '@/contexts/AuthContext';

export const SidebarLayout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user, logout, isAdmin, token } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [pendingCount, setPendingCount] = useState(0);
  const [unreadChat, setUnreadChat] = useState(0);
  const isRTL = i18n.language === 'ar';

  useEffect(() => { applyTheme(getTheme()); }, []);

  useEffect(() => {
    if (!isAdmin || !token) return;
    const fetchCount = () => { const h = { headers: { Authorization: `Bearer ${token}` } }; axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/requests/pending-count`, h).then(r => setPendingCount(r.data.count || 0)).catch(() => {}); axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/chat/unread-count`, h).then(r => setUnreadChat(r.data.count || 0)).catch(() => {}); };
    fetchCount();
    const iv = setInterval(fetchCount, 30000);
    return () => clearInterval(iv);
  }, [isAdmin, token, location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile && !isSidebarOpen) setIsSidebarOpen(true);
      if (mobile && isSidebarOpen) setIsSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) setIsSidebarOpen(false);
  }, [location.pathname]);

  const toggleLanguage = (lang) => {
    if (i18n.language !== lang) {
      localStorage.setItem('appLanguage', lang);
      i18n.changeLanguage(lang).then(() => window.location.reload());
    }
  };

  const allMenuItems = [
    { icon: Home, label: isRTL ? 'الرئيسية' : 'Home', path: '/home', roles: ['client'] },
    { icon: Users, label: isRTL ? (user?.role === 'client' ? 'حسابي' : t('clients')) : (user?.role === 'client' ? 'My Account' : t('clients')), path: '/clients', roles: ['admin', 'client'] },
    { icon: Building2, label: isRTL ? 'المنشآت والموظفين' : 'Companies & Employees', path: '/employees', roles: ['admin', 'client'] },
    { icon: ListChecks, label: isRTL ? 'متابعة المعاملات' : 'Tracking', path: '/tracking', roles: ['admin', 'client'] },
    { icon: Receipt, label: isRTL ? 'كشف الحساب' : 'Statement', path: '/statement', roles: ['client'] },
    { icon: ClipboardList, label: isRTL ? 'خطط العمل' : 'Work Plans', path: '/work-plans', roles: ['admin'] },
    { icon: Inbox, label: isRTL ? 'طلبات التطبيق' : 'App Requests', path: '/requests', roles: ['admin'] },
    { icon: Globe, label: isRTL ? 'المعاملات الخارجية' : 'External transactions', path: '/external', roles: ['admin'] },
    { icon: MessageSquare, label: isRTL ? 'المحادثات' : 'Chat', path: '/chat', roles: ['admin'] },
    { icon: PhoneCall, label: isRTL ? 'سجل المكالمات' : 'Calls log', path: '/calls-log', roles: ['admin'] },
    { icon: Smartphone, label: isRTL ? 'إعدادات التطبيق' : 'App Settings', path: '/app-settings', roles: ['admin'] },
  ];

  const menuItems = allMenuItems.filter(item => item.roles.includes(user?.role || 'client'));
  useEffect(() => { const link = document.querySelector('link[rel="manifest"]'); if (link) link.setAttribute('href', '/admin-manifest.json'); }, []);
  const sidebarWidth = isMobile ? '17rem' : '16.5rem';

  return (
    <div className="flex h-screen bg-slate-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Mobile overlay */}
      {isMobile && isSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        style={{
          position: 'fixed', top: 0,
          [isRTL ? 'right' : 'left']: 0,
          height: '100%',
          width: isSidebarOpen ? sidebarWidth : '0',
          zIndex: 50,
        }}
        className="transition-all duration-300 overflow-hidden"
      >
        <div className="flex flex-col h-full sidebar-animated-bg" 
          style={{ width: sidebarWidth, borderInlineEnd: '1px solid rgba(0,0,0,0.06)' }}>
          
          {/* Logo */}
          <div className="px-5 py-5">
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
                style={{ backgroundColor: 'var(--theme-primary, #0F172A)' }}>
                <span style={{ color: 'var(--theme-accent, #D4AF37)' }} className="font-bold text-lg">A</span>
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-base font-bold text-slate-800 truncate">AZIZI.CO</h1>
                <p className="text-[10px] text-slate-400 font-medium">{isRTL ? 'نظام إدارة الأعمال' : 'Business Management'}</p>
              </div>
              {isMobile && (
                <button onClick={() => setIsSidebarOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
            <p className={`px-3 pt-2 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400 ${isRTL ? 'text-right' : 'text-left'}`}>
              {isRTL ? 'القائمة' : 'Menu'}
            </p>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
              return (
                <Link key={item.path} to={item.path} data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${isRTL ? 'flex-row-reverse' : ''} ${
                    isActive ? 'shadow-sm' : 'hover:bg-slate-100'
                  }`}
                  style={isActive ? {
                    backgroundColor: 'var(--theme-primary, #0F172A)',
                    color: '#fff',
                  } : {}}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                    isActive ? 'bg-white/15' : 'bg-slate-100 group-hover:bg-slate-200'
                  }`}>
                    <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-500'}`} />
                  </div>
                  <span className={`font-medium text-sm ${isActive ? 'text-white' : 'text-slate-700'}`}>{item.label}</span>
                  {item.path === '/chat' && unreadChat > 0 && (
                    <span className={`ms-auto min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center ${isActive ? 'bg-white text-slate-900' : 'bg-red-500 text-white'}`} data-testid="chat-badge">{unreadChat}</span>
                  )}
                  {item.path === '/requests' && pendingCount > 0 && (
                    <span className={`ms-auto min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center ${isActive ? 'bg-white text-slate-900' : 'bg-red-500 text-white'}`} data-testid="requests-badge">{pendingCount}</span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section - separated at bottom */}
          <div className="px-3 pb-4 pt-2" style={{ borderTop: '1px solid #f1f5f9' }}>
            <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl bg-slate-50 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 rounded-xl shrink-0 object-cover" />
              ) : (
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: 'var(--theme-primary, #0F172A)' }}>
                  <span className="text-xs font-bold" style={{ color: 'var(--theme-accent, #D4AF37)' }}>
                    {(user?.username || 'U')[0].toUpperCase()}
                  </span>
                </div>
              )}
              <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : ''}`}>
                <p className="text-sm font-semibold text-slate-800 truncate">{user?.username}</p>
                <p className="text-[10px] text-slate-400 capitalize">{user?.role}</p>
              </div>
              <button onClick={logout} data-testid="logout-btn"
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"
                title={isRTL ? 'تسجيل الخروج' : 'Logout'}>
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col transition-all duration-300 min-w-0"
        style={{ [isRTL ? 'marginRight' : 'marginLeft']: !isMobile && isSidebarOpen ? sidebarWidth : '0' }}>
        
        {/* Top header */}
        <header className={`sticky top-0 z-30 flex h-14 items-center justify-between gap-3 bg-white/80 backdrop-blur-xl px-4 md:px-6 ${isRTL ? 'flex-row-reverse' : ''}`}
          style={{ borderBottom: '1px solid #f1f5f9' }}>
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Button onClick={() => setIsSidebarOpen(!isSidebarOpen)} variant="ghost" size="icon" 
              className="hover:bg-slate-100 h-9 w-9 rounded-xl" data-testid="sidebar-toggle">
              {!isMobile && isSidebarOpen 
                ? (isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />) 
                : <Menu className="w-4 h-4" />}
            </Button>
            <h2 className="text-sm font-semibold text-slate-800 truncate max-w-[160px] md:max-w-none">
              {menuItems.find(item => item.path === location.pathname)?.label ||
               menuItems.find(item => item.path !== '/' && location.pathname.startsWith(item.path))?.label ||
               ''}
            </h2>
          </div>
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <PushToggle isRTL={isRTL} />
            <ThemeCustomizer isRTL={isRTL} />
            <div className="flex items-center gap-0.5 bg-slate-100 rounded-full p-0.5" data-testid="language-toggle-container">
              <button onClick={() => toggleLanguage('ar')} data-testid="lang-ar-btn"
                className={`px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-200 ${
                  i18n.language === 'ar' ? 'text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
                style={i18n.language === 'ar' ? { backgroundColor: 'var(--theme-primary, #0F172A)' } : {}}>
                العربية
              </button>
              <button onClick={() => toggleLanguage('en')} data-testid="lang-en-btn"
                className={`px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-200 ${
                  i18n.language === 'en' ? 'text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
                style={i18n.language === 'en' ? { backgroundColor: 'var(--theme-primary, #0F172A)' } : {}}>
                EN
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-3 md:p-6 lg:p-8 bg-slate-50">{children}</main>
      </div>
      <IncomingCall isRTL={isRTL} />
      <CallbackAlerts isRTL={isRTL} />
    </div>
  );
};
